import React, { useCallback, useMemo, useState } from 'react';
import { GridIcon, SparklesIcon, CheckCircleIcon, TrendingUpIcon, ZapIcon } from 'lucide-react';
import { DuplicateSet, CustomerRecord } from './DuplicateCard';
import { HeatmapGrid } from './HeatmapGrid';
import { ConfidenceSlider } from './ConfidenceSlider';
import { ConfidenceBadge } from './ConfidenceBadge';
type MultiFieldSelection = {
  [fieldName: string]: string | null;
};
type HeatmapMergeViewProps = {
  duplicateSet: DuplicateSet;
  fieldSelections: MultiFieldSelection;
  onFieldSelect: (fieldName: string, recordId: string) => void;
  onSelectPrimary: (recordId: string) => void;
  primaryRecordId: string | null;
  showPreview?: boolean;
};
type FieldConfig = {
  name: keyof CustomerRecord;
  label: string;
  format?: (value: string) => string;
};
const fieldConfigs: FieldConfig[] = [{
  name: 'firstName',
  label: 'First Name'
}, {
  name: 'lastName',
  label: 'Last Name'
}, {
  name: 'email',
  label: 'Email Address'
}, {
  name: 'phone',
  label: 'Phone Number'
}, {
  name: 'address',
  label: 'Address'
}, {
  name: 'loanAmount',
  label: 'Loan Amount',
  format: v => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  }).format(Number(v))
}, {
  name: 'applicationDate',
  label: 'Application Date',
  format: v => new Date(v).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}, {
  name: 'ssn',
  label: 'SSN (Masked)'
}];
export function HeatmapMergeView({
  duplicateSet,
  fieldSelections,
  onFieldSelect,
  onSelectPrimary,
  primaryRecordId,
  showPreview = false
}: HeatmapMergeViewProps) {
  const [confidenceThreshold, setConfidenceThreshold] = useState(70);
  // Guard against undefined duplicateSet
  if (!duplicateSet || !duplicateSet.records) {
    return null;
  }
  const records = duplicateSet.records;
  // Calculate quality score for a cell
  const calculateQualityScore = useCallback((record: CustomerRecord, fieldName: keyof CustomerRecord): number => {
    const value = String(record[fieldName]);
    const isEmpty = !value || value === 'undefined' || value.trim() === '';
    if (isEmpty) return 0;
    let score = 0;
    // Completeness: 40 points
    score += 40;
    // Recency: 30 points
    const recordDate = new Date(record.createdAt).getTime();
    const allDates = records.map(r => new Date(r.createdAt).getTime());
    const maxDate = Math.max(...allDates);
    const minDate = Math.min(...allDates);
    const dateRange = maxDate - minDate;
    if (dateRange > 0) {
      const recencyScore = (recordDate - minDate) / dateRange * 30;
      score += recencyScore;
    } else {
      score += 30;
    }
    // Frequency: 30 points
    const values = records.map(r => String(r[fieldName]));
    const valueCounts = values.reduce((acc, v) => {
      acc[v] = (acc[v] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const maxCount = Math.max(...Object.values(valueCounts));
    const thisCount = valueCounts[value] || 0;
    const frequencyScore = thisCount / maxCount * 30;
    score += frequencyScore;
    return Math.round(score);
  }, [records]);
  // Count golden path recommendations above threshold
  const goldenPathCount = useMemo(() => {
    return fieldConfigs.filter(config => {
      const scores = records.map(record => ({
        recordId: record.id,
        score: calculateQualityScore(record, config.name)
      }));
      const best = scores.reduce((max, curr) => curr.score > max.score ? curr : max, scores[0]);
      return best && best.score >= confidenceThreshold;
    }).length;
  }, [records, confidenceThreshold, calculateQualityScore]);
  // Auto-select golden path
  const handleAutoSelectGoldenPath = useCallback(() => {
    fieldConfigs.forEach(config => {
      const scores = records.map(record => ({
        recordId: record.id,
        score: calculateQualityScore(record, config.name)
      }));
      const best = scores.reduce((max, curr) => curr.score > max.score ? curr : max, scores[0]);
      if (best && best.score >= confidenceThreshold) {
        onFieldSelect(config.name, best.recordId);
      }
    });
    // Set primary to most complete record
    const completenessScores = records.map(record => {
      const filledFields = fieldConfigs.filter(c => {
        const value = String(record[c.name]);
        return value && value !== 'undefined' && value.trim() !== '';
      });
      return {
        recordId: record.id,
        score: filledFields.length
      };
    });
    const mostComplete = completenessScores.reduce((max, curr) => curr.score > max.score ? curr : max, completenessScores[0]);
    if (mostComplete) {
      onSelectPrimary(mostComplete.recordId);
    }
  }, [records, confidenceThreshold, onFieldSelect, onSelectPrimary, calculateQualityScore]);
  return <div className={`flex-1 overflow-y-auto transition-all duration-300 ${showPreview ? 'pb-64' : 'pb-6'}`} data-id="element-137">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10 shadow-sm" data-id="element-138">
        <div className="flex items-center justify-between" data-id="element-139">
          <div className="flex items-center gap-4" data-id="element-140">
            <GridIcon className="w-6 h-6 text-blue-500" aria-hidden="true" data-id="element-141" />
            <div data-id="element-142">
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-143">
                Heatmap Merge View
              </h2>
              <p className="text-sm text-gray-600" data-id="element-144">
                Click cells to select values • Darker colors = higher quality
              </p>
            </div>
          </div>
          <ConfidenceBadge score={duplicateSet.confidenceScore} size="lg" data-id="element-145" />
        </div>
      </div>

      {/* Controls */}
      

      {/* Heatmap Grid */}
      <div className="p-6" data-id="element-146">
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm" data-id="element-147">
          <HeatmapGrid records={records} fieldConfigs={fieldConfigs} fieldSelections={fieldSelections} onCellClick={onFieldSelect} confidenceThreshold={confidenceThreshold} data-id="element-148" />
        </div>
      </div>
    </div>;
}